// Calculs de route et recherche d'adresse (D-053), depuis l'appareil, sans clé :
//  - route : OSRM (router.project-osrm.org, données OpenStreetMap). Usage léger : le staff
//    garde le résultat sur le match, une fois pour tout le monde ;
//  - adresse : Géoplateforme IGN (data.geopf.fr/geocodage), adresses et lieux de France.
// L'itinéraire IGN (data.geopf.fr/navigation) ne répondait pas au 2026-10-27 : à reprendre ici
// si OSRM venait à manquer.
import type { Place } from '../domain/trip';

export async function route(from: Place, to: Place): Promise<{ km: number; min: number }> {
  const r = await fetch(`https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=false&alternatives=false&steps=false`);
  if (!r.ok) throw new Error(`itinéraire indisponible (${r.status})`);
  const j = await r.json();
  const best = j?.routes?.[0];
  if (j?.code !== 'Ok' || !best || !Number.isFinite(best.distance) || !Number.isFinite(best.duration)) throw new Error('pas de route');
  return { km: Math.round(best.distance / 100) / 10, min: Math.round(best.duration / 60) };
}

/** Adresses et lieux qui répondent à une saisie, à choisir dans une liste : d'abord les lieux
 *  (salles, gymnases) proches de `near` (notre gymnase), puis les adresses. */
export async function searchPlaces(q: string, near?: Place | null): Promise<Place[]> {
  const text = q.trim();
  if (text.length < 3) return [];
  const one = async (index: 'poi' | 'address') => {
    const u = new URL('https://data.geopf.fr/geocodage/search');
    u.searchParams.set('q', text.slice(0, 200));
    u.searchParams.set('limit', '5');
    u.searchParams.set('index', index);
    if (near) { u.searchParams.set('lat', String(near.lat)); u.searchParams.set('lon', String(near.lng)); }
    const r = await fetch(u);
    if (!r.ok) throw new Error(`recherche indisponible (${r.status})`);
    const j = await r.json();
    return ((j?.features ?? []) as { geometry?: { coordinates?: [number, number] }; properties?: Record<string, unknown> }[]).map(f => {
      const [lng, lat] = f.geometry?.coordinates ?? [NaN, NaN];
      const p = f.properties ?? {};
      const first = (v: unknown) => (Array.isArray(v) ? String(v[0] ?? '') : v == null ? '' : String(v)).trim();
      let label: string;
      if (index === 'poi') label = [first(p.name), [first(p.postcode), first(p.city)].filter(Boolean).join(' ')].filter(Boolean).join(', ');
      // Une commune seule (« L'Isle-Jourdain ») : le code postal lève l'ambiguïté (32600 ou 86150).
      else label = p.type === 'municipality' && p.postcode ? `${first(p.label)} (${first(p.postcode)})` : first(p.label);
      return { lat: Number(lat), lng: Number(lng), label: label.slice(0, 200) || null };
    });
  };
  const [poi, addr] = await Promise.all([one('poi').catch(() => []), one('address')]);
  return [...poi.slice(0, 4), ...addr.slice(0, 4)].filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
}
